import React, { useState } from 'react';
import moment from 'moment';
import { Divider, Button, message } from 'antd';
import { PlusOutlined, InfoCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';

import { apiTokenStatus } from '~constants/landlord';
import CreateTokenModal from './createTokenModal';
import CreateWebhookModal from './createWebhookModal';
import RevokeModal from './revokeModal';
import PMSChannel from './pms-channel';
import CustomizedPMS from './customized-pms';

const LandlordToken = ({
  t,
  landlord,
  createApiToken,
  updateApiToken,
  accountInfo,
  roleId,
  getLandlord,
  handleGetAPITokens,
  apiTokens,
  updateLandlord,
}) => {
  const [visible, setVisible] = useState(false);
  const [revokeVisible, setRevokeVisible] = useState(false);
  const [webhookVisible, setWebhookVisible] = useState(false);

  const webhook = landlord?.webhook;
  const hasAppliedAPItoken = apiTokens?.[0]?.node?.status === apiTokenStatus.PENDING;

  const handleOpenOrCloseModal = () => {
    setVisible(!visible);
  };

  const handleOpenOrCloseRevokeModal = () => {
    setRevokeVisible(!revokeVisible);
  };

  const handleOpenOrCloseWebhookModal = () => {
    setWebhookVisible(!webhookVisible);
  };

  const handleCreateTokenApi = (label, description) => {
    const variables = {
      description: description ?? '',
      label,
      roleId,
    };

    createApiToken(variables, res => {
      if (res.createApiToken?.id) {
        setVisible(!visible);
        handleGetAPITokens();
        message.success(t('cms.landlord.token.created.successfully'));
      }
    });
  };

  const handleUpdateTokenApi = (label, description) => {
    const variables = {
      description: description ?? '',
      label,
      status: apiTokenStatus.ENABLE,
      id: apiTokens?.[0]?.node?.id,
    };

    updateApiToken(variables, res => {
      if (res.updateApiToken?.id) {
        message.success(t('cms.landlord.token.modified.successfully'));

        setVisible(!visible);
        handleGetAPITokens();
      }
    });
  };

  const revokeToken = () => {
    const variables = {
      status: apiTokenStatus.REVOKE,
      id: apiTokens?.[0]?.node?.id,
    };

    updateApiToken(variables, res => {
      if (res.updateApiToken?.id) {
        message.success(t('cms.landlord.token.revoke.successfully'));

        setRevokeVisible(!revokeVisible);
        handleGetAPITokens();
        getLandlord();
      }
    });
  };

  const handleSuccessCallback = () => {
    getLandlord();
  };

  return (
    <div>
      <div className="landlord-token-wrapper">
        <p className="landlord-token-title">
          {t('cms.landlord.detail.token.landlord_personal_integration')}
        </p>
        <If condition={hasAppliedAPItoken}>
          <div className="landlord-token-tips">
            <InfoCircleFilled className="landlord-token-tips__icon" />
            <span className="landlord-token-tips__content">
              {t('cms.dashboard.landlord.details.token.add_button')}
            </span>
          </div>
        </If>
        <div className="landlord-token">
          <p className="landlord-section-title" style={{ marginBottom: 10 }}>
            {t('cms.dashboard.landlord.details.token.title')}
          </p>

          <Choose>
            <When condition={apiTokens.length === 0 || hasAppliedAPItoken}>
              <div className="empty-container">
                <div className="empty-card">
                  <div className="empty-card__wrapper">
                    <span>{t('cms.landlord.token.empty_no_api_token')}</span>
                  </div>
                  <div className="empty-card__button" onClick={handleOpenOrCloseModal}>
                    <PlusOutlined style={{ marginRight: 8 }} />
                    <span style={{ fontWeight: 600 }}>
                      {t('cms.dashboard.landlord.details.token.create_token')}
                    </span>
                  </div>
                </div>
              </div>
            </When>
            <Otherwise>
              <div className="token-card-container">
                <For of={apiTokens} each="apiToken" index="index">
                  <div className="token-card" key={index}>
                    <div className="token-card__wrapper">
                      <div className="token-card__label">
                        <p className="token-card__label-title">{apiToken?.node?.label}</p>
                        <If condition={apiToken?.node?.description}>
                          <span className="token-card__label-desc">
                            {apiToken?.node?.description}
                          </span>
                        </If>
                      </div>
                      <div className="token-card__token">{apiToken?.node?.apiToken}</div>
                      <div className="token-card__date">
                        {apiToken?.node?.lastAccessedAt
                          ? t('cms.dashboard.landlord.details.token.last_time', {
                              time: moment(apiToken?.node?.lastAccessedAt).format(
                                'YYYY-MM-DD HH:mm',
                              ),
                            })
                          : t('cms.dashboard.landlord.details.token.never_used_so_for')}
                      </div>
                    </div>
                    <div className="token-card__button" onClick={handleOpenOrCloseRevokeModal}>
                      <span style={{ fontWeight: 600 }}>
                        {t('cms.dashboard.landlord.details.token.revoke')}
                      </span>
                    </div>
                  </div>
                </For>
              </div>
            </Otherwise>
          </Choose>

          <If condition={visible}>
            <CreateTokenModal
              t={t}
              landlord={landlord}
              handleCreateTokenApi={handleCreateTokenApi}
              accountInfo={accountInfo}
              visible={visible}
              handleOpenOrCloseModal={handleOpenOrCloseModal}
              hasAppliedAPItoken={hasAppliedAPItoken}
              handleUpdateTokenApi={handleUpdateTokenApi}
            />
          </If>

          <If condition={revokeVisible}>
            <RevokeModal
              t={t}
              revokeToken={revokeToken}
              revokeVisible={revokeVisible}
              handleOpenOrCloseRevokeModal={handleOpenOrCloseRevokeModal}
            />
          </If>
        </div>

        <Divider />

        <div className="landlord-webhook">
          <div>
            <p className="landlord-section-title">
              {t('cms.dashboard.landlord.details.token.webhook.title')}
            </p>
            <p className="landlord-webhook-content">
              {t('cms.dashboard.landlord.details.token.webhook.content.url')}:{' '}
              <span>{webhook?.callbackUrl || '-'}</span>
            </p>
            <If condition={webhook && !webhook?.valid}>
              <p className="landlord-webhook-error">
                <ExclamationCircleFilled style={{ fontSize: 12 }} />
                <span className="landlord-webhook-error-text">
                  {t('cms.dashboard.landlord.details.token.webhook.error')}
                </span>
              </p>
            </If>
          </div>
          <Button
            type="link"
            className="landlord-webhook-btn"
            onClick={handleOpenOrCloseWebhookModal}
          >
            {t(
              `cms.dashboard.landlord.details.token.webhook.${
                webhook?.callbackUrl ? 'edit' : 'add'
              }_btn`,
            )}
          </Button>
        </div>
      </div>

      <PMSChannel t={t} landlord={landlord} getLandlord={getLandlord} />
      <CustomizedPMS
        t={t}
        landlord={landlord}
        getLandlord={getLandlord}
        updateLandlord={updateLandlord}
      />

      <If condition={webhookVisible}>
        <CreateWebhookModal
          t={t}
          visible={webhookVisible}
          landlord={landlord}
          handleOpenOrCloseModal={handleOpenOrCloseWebhookModal}
          handleSuccessCallback={handleSuccessCallback}
        />
      </If>
    </div>
  );
};

export default LandlordToken;
